import ListItemButton from '@mui/material/ListItemButton'

import { styled } from '@mui/material/styles'

// Defaults match `createBrandBorderTokens()` — fall back to them when the
// brand theme tokens are not mounted (e.g. tests rendering without
// BrandProvider, which use MUI's vanilla theme that has no `theme.border`
// nor `theme.palette.border` subtree).
const BORDER_SM = '1px'
const BORDER_MD = '2px'
const BORDER_LG = '3px'

export const StyledListItem = styled(ListItemButton, {
  shouldForwardProp: (prop: string) => prop !== 'error',
})<{
  variant?: 'outlined' | 'small'
  error?: boolean
}>(({ theme, variant, error }) => {
  const borderLg = theme.border?.lg ?? BORDER_LG
  const borderMd = theme.border?.md ?? BORDER_MD
  const paletteBorder = theme.palette.border ?? {}

  return {
    '&.MuiListItemButton-root': {
      gap: theme.spacing(2),
      transition:
        'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out',

      ...(variant === 'small' && {
        padding: 0,
      }),
      '&:hover': {
        backgroundColor: theme.palette.background['brand-subtle'],
      },
      '&:active': {
        backgroundColor: theme.palette.action['bg-selected'],
      },
      '&.Mui-focusVisible': {
        outline: `${borderLg} solid ${paletteBorder.focus}`,
        outlineOffset: `-${borderLg}`,
      },

      ...(variant === 'outlined' && {
        border: `${BORDER_SM} solid ${paletteBorder['brand-default']}`,
        borderRadius: '8px',

        '&:hover': {
          backgroundColor: theme.palette.background['brand-subtle'],
          borderColor: paletteBorder['brand-strong'],
        },

        '&.Mui-focusVisible': {
          border: `${borderLg} solid ${paletteBorder.focus}`,
          outline: 'none',
          padding: '7px 15px',
        },

        '&.Mui-disabled': {
          border: `${BORDER_SM} solid ${paletteBorder.subtle}`,
        },

        '&.Mui-selected': {
          border: `${borderMd} solid ${paletteBorder['brand-strong']}`,
          padding: '7px 15px',
        },

        ...(error && {
          borderColor: paletteBorder.error,
          '&:hover': {
            backgroundColor: 'transparent',
            borderColor: paletteBorder.error,
          },
          '&.Mui-selected': {
            borderColor: paletteBorder.error,
          },
        }),
      }),

      '&.Mui-disabled.Mui-selected': {
        backgroundColor: 'transparent',
        ...(variant === 'outlined' && {
          border: `${BORDER_SM} solid ${paletteBorder.subtle}`,
          padding: '8px 16px',
        }),
      },
    },

    '&.Mui-selected': {
      backgroundColor: theme.palette.action['bg-selected'],

      '&:hover': {
        backgroundColor: theme.palette.action['bg-selected'],
      },
    },
  }
})

export const StyledInputListItem = styled(StyledListItem)({
  '& .MuiCheckbox-root, & .MuiRadio-root': {
    padding: 0,

    '&:hover': {
      backgroundColor: 'unset',
    },
  },

  '&.small': {
    marginRight: 0,
    padding: 0,
  },
})

export const StyledListItemText = styled('div')(({ theme }) => ({
  margin: theme.spacing(1, 0),
  width: '100%',
}))

export const StyledListItemErrorMessage = styled('p')(({ theme }) => ({
  color: theme.palette.text.error,
  fontSize: '12px',
  margin: 0,
}))
